/**
 * 메뉴/아이콘 공용 컨트롤 — 커스텀 스타일 셀렉트 + 시각적 아이콘 피커 + 통합 아이콘 렌더러.
 * 네이티브 select/아이콘 미리보기 대신 토큰 기반 커스텀 UI. 내장 lucide + 커스텀 SVG(레지스트리) 모두 지원.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import {
  LayoutGrid, MessageCircle, Mail, Calendar, Target, Folder, BookOpen, Settings,
  Globe, Bell, Users, FileText, Bot, ChevronDown, Search, Check,
  // 메뉴 레지스트리 씨앗이 쓰는 이름 (2026-10-02) — 아래 LUCIDE_ICONS 머리말 참조.
  Contact, CalendarDays, Clock, UserCog, Wallet, FileCheck, BarChart3, Network,
  HelpCircle, Sparkles, Mic, IdCard,
  type LucideIcon,
} from '../../../icons';

// 메뉴 아이콘으로 쓸 수 있는 내장 lucide 세트(이름→컴포넌트). 커스텀 SVG 는 'custom:{name}'.
//
// ⛔⛔ 2026-10-02 — 이 지도가 «열셋을 몰라서» 콘솔이 메뉴 아이콘을 틀리게 그리고 있었다.
//   씨앗(admin/backend/src/menu-registry.ts)의 23행 중 13행의 icon 이 여기 없어, 아래
//   RenderIcon 의 「?? LayoutGrid」 폴백으로 떨어졌다 ⇒ 주소록 · 예약 · 근태 · 인사 · 급여 ·
//   전자결재 · 업무 보고 · 조직도 · 도움말 · AI · 음성 녹음 · 전자명함 · 통합 검색이
//   «모두 대시보드와 같은 아이콘»으로 보였다(= 아이콘이 구별을 전혀 못 했다).
//   ★ 씨앗 주석이 그중 Sparkles 를 «이미 경고하고 있었다»(「없으면 LayoutGrid 로 떨어져
//   대시보드와 같은 아이콘이 둘 뜬다」) — 8443 목록에는 넣고 이 지도에는 안 넣었다.
//
// ⭐ 임시 아이콘이 «아니다». 재서 확인했다 — 그 13 이름이 lucide 0.460.0 의 export 목록에
//   전부 있다(별칭 export 포함: ChartColumnBig as BarChart3 · CircleHelp as HelpCircle).
//   ⇒ 그래서 비슷한 것으로 «대신»하지 않고 제 이름 그대로 가져왔다. 임시 매핑 0건.
//
// ⚠ 지도가 둘이다 — 8443 은 apps/frontend/src/components/NavBar/NavBar.tsx 의 ICON_BY_NAME.
//   두 지도는 «일부러» 다르다: 8443 은 section 'mobile' 행을 거르므로(menu-config 의 isWebMenuRow)
//   그 4행(알림·음성 녹음·전자명함·통합 검색)의 아이콘을 몰라도 된다. ★ 이 콘솔 지도는
//   «모든» 행을 그리므로 23행 전부를 알아야 한다. 그 둘을 menu-icon-map.test.ts 가 잠근다.
export const LUCIDE_ICONS: Record<string, LucideIcon> = {
  LayoutGrid, MessageCircle, Mail, Calendar, Target, Folder, BookOpen, Settings,
  Globe, Bell, Users, FileText, Bot,
  // 2026-10-02 보강 — 씨앗 23행이 쓰는 나머지. 차례는 씨앗의 sort_order 와 같게 둔다.
  Contact, CalendarDays, Clock, UserCog, Wallet, FileCheck, BarChart3, Network,
  HelpCircle, Sparkles, Mic, IdCard, Search,
};
export const LUCIDE_NAMES = Object.keys(LUCIDE_ICONS);

/** 아이콘 렌더 — 'custom:{name}' 이면 레지스트리 SVG, 아니면 lucide 컴포넌트. */
export function RenderIcon({ name, size = 20, customSvg }: { name: string; size?: number; customSvg?: Record<string, string> }) {
  if (name?.startsWith('custom:')) {
    const svg = customSvg?.[name];
    if (svg) {
      return (
        <span
          className="svc-svgicon"
          style={{ width: size, height: size }}
          // eslint-disable-next-line react/no-danger -- 백엔드 sanitizeSvg 로 정제된 SVG
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      );
    }
    return <LayoutGrid size={size} />;
  }
  const C = LUCIDE_ICONS[name] ?? LayoutGrid;
  return <C size={size} />;
}

function useOutsideClose(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onDoc(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); }
    function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [onClose]);
  return ref;
}

export interface SelectOption { value: string; label: string }

/**
 * 커스텀 스타일 셀렉트(네이티브 select 미사용).
 *
 * `portal` 활성 시 드롭다운 메뉴를 document.body 로 createPortal + position:fixed 로 렌더한다.
 * → 부모의 overflow:hidden / 스크롤 컨테이너 / stacking context 에 잘리지 않고 **항상 최상단**에
 *   완전히 보인다. 아래 공간이 부족하면 위로 flip 하고, maxHeight 를 뷰포트에 맞춰 콘텐츠가
 *   항상 보이도록 한다. portal 미지정 시 기존 absolute 동작 그대로(회귀 0).
 */
export function StyledSelect({ value, options, onChange, disabled, placeholder, portal }: {
  value: string; options: SelectOption[]; onChange: (v: string) => void; disabled?: boolean; placeholder?: string; portal?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const cur = options.find((o) => o.value === value);

  // 버튼 위치 기준 fixed 좌표 계산 + 공간 부족 시 위로 flip.
  const place = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const openUp = spaceBelow < 240 && spaceAbove > spaceBelow;
    const maxH = Math.max(120, Math.min(320, (openUp ? spaceAbove : spaceBelow) - gap - 8));
    setMenuStyle({
      position: 'fixed',
      left: Math.round(r.left),
      width: Math.round(r.width),
      maxHeight: maxH,
      zIndex: 2000,
      ...(openUp
        ? { bottom: Math.round(window.innerHeight - r.top + gap) }
        : { top: Math.round(r.bottom + gap) }),
    });
  }, []);

  useLayoutEffect(() => {
    if (open && portal) place();
  }, [open, portal, place]);

  // 열려 있는 동안 스크롤/리사이즈에 위치 추종 + 외부 클릭/ESC 로 닫기(메뉴·버튼 클릭은 제외).
  useEffect(() => {
    if (!open) return;
    const reposition = () => { if (portal) place(); };
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    if (portal) {
      window.addEventListener('scroll', reposition, true);
      window.addEventListener('resize', reposition);
    }
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, portal, place]);

  const menu = (
    <ul
      ref={menuRef}
      className={`svc-select__menu${portal ? ' svc-select__menu--portal' : ''}`}
      role="listbox"
      style={portal ? menuStyle : undefined}
    >
      {options.map((o) => (
        <li key={o.value} role="option" aria-selected={o.value === value}
          className={`svc-select__opt${o.value === value ? ' is-active' : ''}`}
          onClick={() => { onChange(o.value); setOpen(false); }}>
          <span>{o.label}</span>
          {o.value === value && <Check size={14} />}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="svc-select" ref={wrapRef}>
      <button ref={btnRef} type="button" className="svc-select__btn" disabled={disabled} aria-haspopup="listbox" aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}>
        <span className={`svc-select__val${cur ? '' : ' is-placeholder'}`}>{cur?.label ?? placeholder ?? '선택'}</span>
        <ChevronDown size={16} className={`svc-select__chev${open ? ' is-open' : ''}`} />
      </button>
      {open && (portal ? createPortal(menu, document.body) : menu)}
    </div>
  );
}

/** 시각적 아이콘 피커 — 내장 lucide + 커스텀 아이콘을 이미지로 보여주고 선택. */
export function IconPicker({ value, onChange, customSvg, customNames }: {
  value: string;
  onChange: (v: string) => void;
  customSvg?: Record<string, string>;
  customNames?: string[]; // ['custom:foo', ...]
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useOutsideClose(() => setOpen(false));
  const all = [...LUCIDE_NAMES, ...(customNames ?? [])];
  const filtered = q.trim()
    ? all.filter((n) => n.toLowerCase().includes(q.trim().toLowerCase()))
    : all;
  return (
    <div className="svc-iconpick" ref={ref}>
      <button type="button" className="svc-iconpick__btn" aria-haspopup="dialog" aria-expanded={open}
        onClick={() => setOpen((o) => !o)}>
        <span className="svc-iconpick__cur"><RenderIcon name={value} size={20} customSvg={customSvg} /></span>
        <span className="svc-iconpick__name">{value || '아이콘 선택'}</span>
        <ChevronDown size={16} className={`svc-select__chev${open ? ' is-open' : ''}`} />
      </button>
      {open && (
        <div className="svc-iconpick__pop" role="dialog">
          <div className="svc-iconpick__search">
            <Search size={14} />
            <input autoFocus value={q} placeholder="아이콘 검색…" onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="svc-iconpick__grid">
            {filtered.map((n) => (
              <button key={n} type="button" title={n}
                className={`svc-iconpick__cell${n === value ? ' is-active' : ''}`}
                onClick={() => { onChange(n); setOpen(false); }}>
                <RenderIcon name={n} size={20} customSvg={customSvg} />
              </button>
            ))}
            {filtered.length === 0 && <span className="svc-iconpick__empty">검색 결과 없음</span>}
          </div>
          {(customNames?.length ?? 0) > 0 && (
            <div className="svc-iconpick__hint">커스텀 아이콘은 “아이콘 관리”에서 추가됩니다.</div>
          )}
        </div>
      )}
    </div>
  );
}
